import { RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

interface UpdateFloatingCardProps {
  onRefresh: () => void;
  onDismiss: () => void;
  isVisible?: boolean;
  sidebarCollapsed?: boolean;
}

export function UpdateFloatingCard({ onRefresh, onDismiss, isVisible = true, sidebarCollapsed = false }: UpdateFloatingCardProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Small delay to ensure the component is mounted before starting animation
    const timer = setTimeout(() => setIsAnimating(true), 10);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setIsAnimating(false);
    // Delay the actual dismiss to allow animation
    setTimeout(() => onDismiss(), 400);
  };

  // Calculate left position based on sidebar state
  const leftPosition = sidebarCollapsed
    ? "left-4 md:left-[72px]" // 56px sidebar width + 16px margin = 72px
    : "left-4 md:left-[240px]"; // 224px sidebar width + 16px margin = 240px

  return (
    <div
      className={`fixed bottom-4 z-[50] transition-all duration-500 ease-in-out ${leftPosition}`}
      style={{
        transform: isAnimating ? 'translateX(0)' : 'translateX(-100%)',
        opacity: isAnimating ? 1 : 0,
      }}
    >
      <div className="bg-background border border-border rounded-lg shadow-lg p-3 max-w-xs backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1 bg-gradient-to-br from-primary/20 to-accent/20 rounded">
            <Sparkles className="h-3 w-3 text-primary" />
          </div>
          <h4 className="text-xs font-medium text-foreground">
            Update Available
          </h4>
        </div>

        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          We've made improvements! Refresh to see what's new.
        </p>

        <div className="flex gap-2">
          <Button
            onClick={handleDismiss}
            variant="ghost"
            size="sm"
            className="flex-1 text-xs h-7 hover:bg-accent hover:text-accent-foreground transition-all duration-200 hover:scale-[1.02]"
          >
            Dismiss
          </Button>
          <Button
            onClick={onRefresh}
            size="sm"
            className="flex-1 flex items-center gap-1 text-xs h-7 hover:shadow-md transition-all duration-200 hover:scale-[1.02]"
          >
            <RefreshCw className="h-3 w-3" />
            Refresh
          </Button>
        </div>
      </div>
    </div>
  );
}